(() => {
  if (customElements.get('pr-policy-benefits')) return;

  class PrPolicyBenefits extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('.pr-policy-benefits__items');
      if (!this.track) return;

      this.progress = this.querySelector('[data-pr-policy-progress]');
      this.progressThumb = this.querySelector('[data-pr-policy-progress-thumb]');
      this.progressTimer = null;
      this.progressFrame = null;

      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.queueProgressUpdate.bind(this);

      this.track.addEventListener('keydown', this.onKeyDown);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.progress && this.progressThumb) {
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.onResize);
        }
        this.queueProgressUpdate();
      }

      this.initialized = true;
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.track?.removeEventListener('keydown', this.onKeyDown);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.progressTimer);
      window.cancelAnimationFrame(this.progressFrame);
      this.initialized = false;
    }

    isMobileSlider() {
      return this.dataset.mobileLayout === 'slider' && window.matchMedia('(max-width: 768px)').matches;
    }

    handleScroll() {
      if (!this.isMobileSlider() || !this.progress) return;

      this.queueProgressUpdate();
      this.progress.classList.add('is-active');
      window.clearTimeout(this.progressTimer);
      this.progressTimer = window.setTimeout(() => {
        this.progress.classList.remove('is-active');
      }, 800);
    }

    queueProgressUpdate() {
      if (!this.progress || !this.progressThumb || this.progressFrame) return;

      this.progressFrame = window.requestAnimationFrame(() => {
        this.progressFrame = null;
        this.updateProgress();
      });
    }

    updateProgress() {
      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const isOverflowing = maximumScroll > 1;
      this.progress.classList.toggle('is-overflowing', isOverflowing);

      if (!isOverflowing) {
        this.progress.classList.remove('is-active');
        return;
      }

      const visibleRatio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
      const scrollProgress = Math.min(1, Math.max(0, this.track.scrollLeft / maximumScroll));
      const thumbWidth = visibleRatio * 100;
      const thumbOffset = scrollProgress * (100 - thumbWidth);

      this.progress.style.setProperty('--pr-policy-progress-width', `${thumbWidth}%`);
      this.progress.style.setProperty('--pr-policy-progress-offset', `${thumbOffset}%`);
    }

    handleKeyDown(event) {
      if (!this.isMobileSlider() || event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        return;
      }

      const firstCard = this.track.querySelector('.pr-policy-benefits__card');
      if (!firstCard) return;

      event.preventDefault();
      const gap = Number.parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.track.scrollBy({
        left: direction * (firstCard.getBoundingClientRect().width + gap),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    handleBlockSelect(event) {
      if (!this.isMobileSlider()) return;

      const card = event.target.closest('.pr-policy-benefits__card');
      if (!card || !this.contains(card)) return;

      card.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }

  customElements.define('pr-policy-benefits', PrPolicyBenefits);
})();
